import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]), swapped in for
// the MUI icon-font set this file used before. Iconsax's default `Linear` variant is already
// pure outline, so both the old filled ImageIcon and VideocamOutlinedIcon map to the same
// Gallery/VideoSquare glyphs used elsewhere in this Inbox folder (CatalogMessageCard.jsx).
import { Gallery, VideoSquare } from "iconsax-react";

const RADIUS = "12px";
/** Default card width (sm+). xs uses narrower cards for mobile inbox / preview. */
const CARD_W = 248;
const CARD_W_XS = 188;
/** Hero image / media block height (sm+). */
const HERO_H_SM = 148;
const HERO_H_XS = 96;
const PILL_RADIUS = "10px";

/** Messenger / Instagram DM structured attachment — colors were hardcoded light-mode-only
 * hex, rendering as "weird white" cards regardless of app theme (Rahim, 2026-08-25: "fix
 * colour scheme for the dark mood"). Every color used across this file now comes from here.
 *
 * Later reconsidered (Rahim, 2026-08-25, after the card went dark in dark mode): "i dont
 * like the template colour black for templates i wanted white background colour in dark
 * mood" — so `isDark` is currently unused on purpose, kept as a param so a real per-mode
 * scheme is a one-line change if this gets revisited. */
function getMetaTokens(_isDark) {
  return {
    CARD_BG: "#FFFFFF",
    BORDER: "#DADCDE",
    DIVIDER: "#E6E7EB",
    TEXT: "#050505",
    SUB: "#65676B",
    /** Meta Messenger / Instagram: CTA tray + pill buttons (not flat all-white). */
    PANEL_BG: "#F0F2F5",
    PILL_BG: "#FFFFFF",
  };
}

/** Compact layout below theme `sm` (default 600px): phones / narrow inbox. `sm+` keeps original sizing. */
const cardWidthSx = { xs: CARD_W_XS, sm: CARD_W };
const heroHeightSx = { xs: HERO_H_XS, sm: HERO_H_SM };

const emojiSplitRegex =
  /([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u;
const emojiOnlyRegex =
  /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;

function renderEmojiLine(text, keyPrefix, sx) {
  const s = text == null ? "" : String(text);
  return (
    <Typography key={keyPrefix} component="div" sx={sx}>
      {s.split(emojiSplitRegex).map((part, idx) => {
        if (part && emojiOnlyRegex.test(part)) {
          return (
            <span key={idx} data-emoji>
              {part}
            </span>
          );
        }
        return part;
      })}
    </Typography>
  );
}

function ctaLabel(btn) {
  return (btn && (btn.title || btn.text || "")).trim() || "Button";
}

/** Button template (IG/FB): all-white chrome; other templates keep gray tray (`PANEL_BG`). */
function MetaButtonStack({ buttons, startWithDivider = true, whiteTray = true }) {
  const theme = useTheme();
  const t = getMetaTokens(theme.palette.mode === "dark");
  if (!buttons || !buttons.length) return null;
  const trayBg = whiteTray ? t.CARD_BG : t.PANEL_BG;
  return (
    <Box
      sx={{
        bgcolor: trayBg,
        display: "flex",
        flexDirection: "column",
        // Pills grew taller (bigger padding) in the last pass but the gap between them
        // didn't scale with it, so they read as smushed together (Rahim, 2026-08-25:
        // "no proper spacing... one pill is being smushed into other").
        gap: "12px",
        width: "100%",
        boxSizing: "border-box",
        px: "12px",
        py: "10px",
        ...(startWithDivider ? { borderTop: `1px solid ${t.DIVIDER}` } : {}),
      }}
    >
      {buttons.map((btn, index) => {
        const type = (btn.type || "postback").toLowerCase();
        const isUrl = type === "web_url";
        const hasHref = isUrl && btn.url && String(btn.url).trim();
        return (
          <Box
            key={index}
            component={hasHref ? "a" : "div"}
            {...(hasHref
              ? { href: btn.url, target: "_blank", rel: "noopener noreferrer" }
              : {})}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              boxSizing: "border-box",
              bgcolor: t.PILL_BG,
              borderRadius: PILL_RADIUS,
              py: "11px",
              px: "16px",
              fontSize: "0.875rem",
              fontWeight: 400,
              color: t.TEXT,
              WebkitTextFillColor: t.TEXT,
              textDecoration: "none",
              textAlign: "center",
              cursor: hasHref ? "pointer" : "default",
              border: `1px solid ${t.BORDER}`,
              boxShadow: theme.palette.mode === "dark" ? "none" : "0 1px 2px rgba(0,0,0,0.04)",
              "&:hover": hasHref ? { textDecoration: "underline", opacity: 0.92, color: t.TEXT } : {},
            }}
          >
            <Box component="span" sx={{ textAlign: "center", wordBreak: "break-word", maxWidth: "100%" }}>
              {ctaLabel(btn)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * @param {{ template: object, fromMe?: boolean, platform?: 'instagram'|'facebook', inboxStructuredTemplateSizing?: boolean }} props
 * When `inboxStructuredTemplateSizing` is true (inbox only), button templates get the same fixed card width as generic/media (`cardWidthSx`).
 */
const MetaStructuredTemplateDisplay = ({
  template,
  fromMe: _fromMe = false,
  platform: _platform,
  inboxStructuredTemplateSizing = false,
}) => {
  const [mediaImageError, setMediaImageError] = React.useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const t = getMetaTokens(isDark);

  React.useEffect(() => {
    setMediaImageError(false);
  }, [template]);

  const tType = (template.template_type || "").toLowerCase();
  const text = template.text || "";
  const buttons = Array.isArray(template.buttons) ? template.buttons : [];
  const elements = Array.isArray(template.elements) ? template.elements : [];
  const topElementStyle = (template.top_element_style || "compact").toLowerCase();

  const shellSx = {
    maxWidth: {
      xs: `min(${CARD_W_XS + 16}px, calc(100vw - 32px))`,
      sm: `min(${CARD_W + 24}px, calc(var(--chatbox-width, 100vw) - 72px))`,
    },
    width: "100%",
    borderRadius: RADIUS,
    border: `1px solid ${t.BORDER}`,
    overflow: "hidden",
    bgcolor: t.CARD_BG,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  };

  /* ——— Button ——— */
  if (tType === "button") {
    const buttonRootSx =
      inboxStructuredTemplateSizing
        ? {
            ...shellSx,
            width: cardWidthSx,
            minWidth: cardWidthSx,
            maxWidth: cardWidthSx,
            boxSizing: "border-box",
          }
        : shellSx;
    return (
      <Box
        sx={{
          ...buttonRootSx,
          bgcolor: t.CARD_BG,
          color: t.TEXT,
        }}
      >
        {text ? (
          <Box
            sx={{
              bgcolor: t.CARD_BG,
              px: "12px",
              py: "10px",
            }}
          >
            {renderEmojiLine(text, "bt", {
              p: 0,
              // Flat 0.9375rem, matching the regular/carousel template body convention
              // (Rahim, 2026-08-25: "fix the font and spacing and padding").
              fontSize: "0.9375rem",
              lineHeight: 1.4,
              color: t.TEXT,
              WebkitTextFillColor: t.TEXT,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              textAlign: "left",
              fontWeight: 400,
              "& span[data-emoji]": {
                fontSize: { xs: "1.15rem", sm: "1.35rem" },
                lineHeight: 1,
                verticalAlign: "middle",
              },
            })}
          </Box>
        ) : null}
        <MetaButtonStack
          buttons={buttons}
          startWithDivider={!!text}
          whiteTray
        />
      </Box>
    );
  }

  /* ——— Generic (horizontal carousel) ——— */
  if (tType === "generic" && elements.length > 0) {
    return (
      // Same treatment as the WhatsApp carousel: one card fills the shell width at a time,
      // horizontal scroll-snap reveals the rest (Rahim, 2026-08-25: "use same type of
      // horizontal scroll", "better spacing and padding").
      <Box sx={shellSx}>
        {text ? (
          <Typography
            component="div"
            sx={{
              fontSize: "0.9375rem",
              lineHeight: 1.4,
              color: t.TEXT,
              padding: "10px 12px 6px 12px",
              textAlign: "left",
            }}
          >
            {text}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            scrollSnapType: "x mandatory",
            // scroll-snap-align only respects `scroll-padding`, not regular `padding` — without
            // this the browser auto-scrolls to cancel out the inset on load.
            scrollPaddingInline: "12px",
            gap: "8px",
            overflowX: "auto",
            padding: "4px 12px 12px 12px",
            scrollbarWidth: "thin",
            WebkitOverflowScrolling: "touch",
            // `scrollbar-color` is inherited from the Inbox's dark-mode scroll pane
            // (`slimScrollbarSx`, "#404040 #121212") — modern Chrome honors that standard
            // property over the legacy `::-webkit-scrollbar-*` pseudo-elements below, so
            // without this override the inherited near-black value painted a solid dark bar
            // (Rahim, 2026-08-25: "fix the scroll bar being black"). Card is fixed-light
            // always, so the scrollbar is pinned light too, not theme-conditional.
            scrollbarColor: "rgba(0,0,0,0.15) transparent",
            "&::-webkit-scrollbar": { height: 3 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#00000026", borderRadius: 2 },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          }}
        >
          {elements.slice(0, 10).map((el, i) => {
            const elButtons = Array.isArray(el.buttons) ? el.buttons : [];
            const hasTextBlock = !!(el.title || el.subtitle);
            return (
              <Box
                key={i}
                sx={{
                  flex: "0 0 100%",
                  scrollSnapAlign: "start",
                  borderRadius: "10px",
                  border: `1px solid ${t.BORDER}`,
                  overflow: "hidden",
                  bgcolor: t.CARD_BG,
                }}
              >
                {el.image_url ? (
                  <Box
                    component="img"
                    src={el.image_url}
                    alt=""
                    sx={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      display: "block",
                      objectFit: "cover",
                      bgcolor: t.PANEL_BG,
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      aspectRatio: "4 / 3",
                      bgcolor: t.PANEL_BG,
                    }}
                  >
                    <Gallery size={30} color={t.SUB} variant="Linear" />
                  </Box>
                )}
                {hasTextBlock ? (
                  <Box sx={{ padding: "8px 10px" }}>
                    {el.title ? (
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, color: t.TEXT, lineHeight: 1.25 }}>
                        {el.title}
                      </Typography>
                    ) : null}
                    {el.subtitle ? (
                      <Typography sx={{ fontSize: "0.8125rem", color: t.SUB, mt: el.title ? 0.35 : 0, lineHeight: 1.3 }}>
                        {el.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}
                <MetaButtonStack
                  buttons={elButtons}
                  startWithDivider={!!el.image_url && !hasTextBlock}
                  whiteTray
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  /* ——— Media ——— */
  if (tType === "media" && elements.length > 0) {
    const el = elements[0];
    const mediaUrlRaw = el.url || el.image_url;
    const mediaUrl = mediaUrlRaw != null && String(mediaUrlRaw).trim() ? String(mediaUrlRaw).trim() : "";
    const hasAttachmentId = !!(el.attachment_id && String(el.attachment_id).trim());
    const showMediaRegion = !!(mediaUrl || hasAttachmentId);
    const mediaType = (el.media_type || "image").toLowerCase();
    const mediaButtons = Array.isArray(el.buttons) ? el.buttons : [];
    const showFallback = mediaImageError;

    const mediaCardSx = {
      ...shellSx,
      width: cardWidthSx,
      minWidth: cardWidthSx,
      maxWidth: cardWidthSx,
      boxSizing: "border-box",
    };
    const placeholderSx = {
      width: "100%",
      height: heroHeightSx,
      minHeight: heroHeightSx,
      maxHeight: heroHeightSx,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: t.SUB,
      bgcolor: t.PANEL_BG,
    };

    return (
      <Box sx={mediaCardSx}>
        {showMediaRegion ? (
          <Box sx={{ width: "100%", lineHeight: 0, bgcolor: t.PANEL_BG }}>
            {mediaUrl ? (
              mediaType === "video" ? (
                <Box
                  component="video"
                  src={mediaUrl}
                  controls
                  sx={{ width: "100%", maxHeight: { xs: 132, sm: 220 }, display: "block" }}
                />
              ) : (
                <>
                  {!showFallback ? (
                    <Box
                      component="img"
                      src={mediaUrl}
                      alt=""
                      sx={{
                        width: "100%",
                        height: heroHeightSx,
                        display: "block",
                        objectFit: "cover",
                        bgcolor: t.PANEL_BG,
                      }}
                      onError={() => setMediaImageError(true)}
                    />
                  ) : (
                    <Box sx={placeholderSx}>
                      <Box sx={{ width: { xs: 28, sm: 40 }, height: { xs: 28, sm: 40 }, display: "flex", opacity: 0.45 }}>
                        <Gallery size="100%" color="currentColor" variant="Linear" />
                      </Box>
                    </Box>
                  )}
                </>
              )
            ) : (
              <Box sx={placeholderSx}>
                {mediaType === "video" ? (
                  <Box sx={{ width: { xs: 28, sm: 40 }, height: { xs: 28, sm: 40 }, display: "flex", opacity: 0.45 }}>
                    <VideoSquare size="100%" color="currentColor" variant="Linear" />
                  </Box>
                ) : (
                  <Box sx={{ width: { xs: 28, sm: 40 }, height: { xs: 28, sm: 40 }, display: "flex", opacity: 0.45 }}>
                    <Gallery size="100%" color="currentColor" variant="Linear" />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        ) : null}
        <MetaButtonStack buttons={mediaButtons} startWithDivider={showMediaRegion} />
      </Box>
    );
  }

  /* ——— List ——— */
  if (tType === "list" && elements.length > 0) {
    return (
      // "list" has no body `text` and short row titles ("Order #1234") — with only a
      // maxWidth cap the shrink-to-fit box just hugs its narrowest content instead of
      // reaching that cap like the other sub-types (which always have a wide image/longer
      // text forcing them out to the full width). Pinning minWidth = maxWidth forces the
      // same card width every other Meta template type lands on anyway (Rahim, 2026-08-25:
      // "why does its width so small and other are perfect... using wrong width").
      <Box sx={{ ...shellSx, minWidth: shellSx.maxWidth }}>
        {elements.map((el, i) => {
          const isFirst = i === 0;
          const largeFirst = topElementStyle === "large" && isFirst;
          return (
            <Box key={i}>
              {largeFirst ? (
                <>
                  {el.image_url ? (
                    <Box
                      component="img"
                      src={el.image_url}
                      alt=""
                      sx={{
                        width: "100%",
                        height: { xs: 112, sm: 168 },
                        objectFit: "cover",
                        display: "block",
                        bgcolor: t.PANEL_BG,
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: { xs: 112, sm: 168 },
                        bgcolor: t.PANEL_BG,
                      }}
                    >
                      <Gallery size={30} color={t.SUB} variant="Linear" />
                    </Box>
                  )}
                  <Box
                    sx={{
                      padding: "12px",
                      borderTop: `1px solid ${t.DIVIDER}`,
                      bgcolor: t.CARD_BG,
                    }}
                  >
                    {el.title ? (
                      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 400, color: t.TEXT }}>
                        {el.title}
                      </Typography>
                    ) : null}
                    {el.subtitle ? (
                      <Typography sx={{ fontSize: "0.8125rem", color: t.SUB, mt: el.title ? 0.5 : 0 }}>
                        {el.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "10px 12px",
                    borderTop: i > 0 ? `1px solid ${t.DIVIDER}` : "none",
                    gap: "10px",
                    bgcolor: t.CARD_BG,
                  }}
                >
                  {el.image_url ? (
                    <Box
                      component="img"
                      src={el.image_url}
                      alt=""
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                        bgcolor: t.PANEL_BG,
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : null}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {el.title ? (
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 400, color: t.TEXT, lineHeight: 1.25 }}>
                        {el.title}
                      </Typography>
                    ) : null}
                    {el.subtitle ? (
                      <Typography sx={{ fontSize: "0.8125rem", color: t.SUB, mt: el.title ? 0.25 : 0, lineHeight: 1.3 }}>
                        {el.subtitle}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
        <MetaButtonStack buttons={buttons} startWithDivider={false} />
      </Box>
    );
  }

  /* ——— Product (minimal Meta-like strip) ——— */
  if (tType === "product" && elements.length > 0) {
    return (
      <Box
        sx={{
          ...shellSx,
          maxWidth: {
            xs: `min(${CARD_W_XS * 2 + 16}px, calc(100vw - 24px))`,
            sm: `min(${CARD_W * 2 + 24}px, calc(var(--chatbox-width, 100vw) - 48px))`,
          },
          bgcolor: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: "10px", overflowX: "auto", maxWidth: "100%", padding: "12px" }}>
          {elements.slice(0, 10).map((el, i) => (
            <Box
              key={i}
              sx={{
                flex: "0 0 auto",
                width: 200,
                minWidth: 200,
                maxWidth: 200,
                borderRadius: RADIUS,
                border: `1px solid ${t.BORDER}`,
                bgcolor: t.CARD_BG,
                padding: "12px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              <Typography sx={{ fontSize: "0.8125rem", color: t.SUB }}>Product</Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: t.TEXT,
                  mt: 0.5,
                  wordBreak: "break-all",
                }}
              >
                {el.id || "—"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  /* ——— Fallback: unknown template_type ——— */
  return (
    <Box sx={{ ...shellSx, p: "12px" }}>
      <Typography variant="body2" sx={{ fontSize: "0.875rem", color: t.SUB }}>
        {template.template_type ? `Template (${template.template_type})` : "Template"}
      </Typography>
    </Box>
  );
};

export default MetaStructuredTemplateDisplay;
